import React from 'react';
import styled from 'styled-components';
import { gql } from '@apollo/client';
import { NextPage } from 'next';
import Link from 'next/link';
import { FOOTER_QUERY } from '../components/footer';
import { HEADER_QUERY, Header } from '../components/header';
import { ContentStackClient } from '../lib/contentstack';
import { Partners } from '../types/contentstack';
import { IPageProps } from '../types/page';
import { Footer } from '../components/footer';
import { mediaScreen } from '../utils/mediaScreen';
import { getAssetUrl } from '../utils/contentstack';
import { Col, ResponsiveContainer, Row } from '../components/responsive';
import { Button } from '../components/ui/button';
import { Icon } from '../components/ui/icon';
import { LogoCarousel } from '../components/logo-carousel';
import {
  HeroEyebrow,
  HeroHeadline,
  HeroSubheadline,
  SectionEyebrow,
  SectionHeadline,
  SectionSubheadline,
} from '../components/section';
import { PartnerCaseStudy } from '../components/partner-case-study';
import { OptimizedImage } from '../components/ui/optimizedImage';

export const PartnersPage: NextPage<IPageProps<Partners>> = ({
  header,
  page,
  footer,
}) => {
  const logos = (page?.partner_logosConnection?.edges || [])
    .filter((edge) => edge?.node?.url)
    .map((edge) => ({
      file: edge!.node!,
    }));

  return (
    <>
      <Header
        pageTitle={page?.title}
        pageDescription={page?.description}
        forceBackground={true}
        {...header}
      />

      {/* Hero */}
      {page?.hero && (
        <Hero>
          <div className="fade" />
          <div className="image">
            <OptimizedImage
              src={getAssetUrl(page.hero.imageConnection)}
              height={800}
              width={750}
            />
          </div>

          <ResponsiveContainer className="content">
            <HeroEyebrow>{page.hero.eyebrow}</HeroEyebrow>
            <HeroHeadline
              dangerouslySetInnerHTML={{ __html: page.hero.headline || '' }}
            />
            <HeroSubheadline>{page.hero.subheadline}</HeroSubheadline>

            <div className="buttons">
              {page.hero.primary_button?.title && (
                <Link
                  href={page.hero.primary_button.href || ''}
                  legacyBehavior
                  passHref
                >
                  <Button as="a" mType="primary">
                    {page.hero.primary_button.title}
                  </Button>
                </Link>
              )}
              {page.hero.secondary_button?.title && (
                <Link
                  href={page.hero.secondary_button.href || ''}
                  legacyBehavior
                  passHref
                >
                  <Button as="a" mType="secondary">
                    {page.hero.secondary_button.title}
                  </Button>
                </Link>
              )}
            </div>
          </ResponsiveContainer>
        </Hero>
      )}

      {/* Partner Logos */}
      {logos.length > 0 && (
        <LogoStripSection>
          <LogoCarousel data={logos} height={80} />
        </LogoStripSection>
      )}

      {/* Section 1: Featured Partners Intro */}
      {page?.section_1 && (
        <Section1>
          <ResponsiveContainer>
            <Row>
              <Col xs={12} md={5}>
                <SectionEyebrow invert>{page.section_1.eyebrow}</SectionEyebrow>
                <SectionHeadline invert>
                  {page.section_1.headline}
                </SectionHeadline>
                <SectionSubheadline invert>
                  {page.section_1.subheadline}
                </SectionSubheadline>
              </Col>
              <Col xs={12} md={7} className="accent-col">
                <img
                  src="/img/built-mapped.svg"
                  alt=""
                  className="accent-image"
                />
              </Col>
            </Row>
          </ResponsiveContainer>
        </Section1>
      )}

      {/* Section 2:  */}
      {/* {page?.section_2?.eyebrow && (
        <Section2>
          <Row>
            <Col xs={12} md={7} className="image">
              <img src={getAssetUrl(page?.section_2?.imageConnection)} />
            </Col>

            <Col xs={12} md={5} className="texts">
              <SectionEyebrow>{page?.section_2?.eyebrow}</SectionEyebrow>
              <SectionHeadline>{page?.section_2?.headline}</SectionHeadline>
              <SectionSubheadline>
                {page?.section_2?.subheadline}
              </SectionSubheadline>

              <Link
                href={page?.section_2?.primary_button?.href || ''}
                legacyBehavior
                passHref
              >
                <Button mType="primary">
                  {page?.section_2?.primary_button?.title}
                </Button>
              </Link>
            </Col>
          </Row>
        </Section2>
      )} */}

      {/* Section 3: Partner Case Studies */}
      {page?.section_3?.cases?.map((caseItem, index) => {
        if (!caseItem) return null;
        return (
          <React.Fragment key={caseItem.headline || `case-${index}`}>
            {index > 0 && (
              <CaseDivider>
                <ResponsiveContainer>
                  <hr />
                </ResponsiveContainer>
              </CaseDivider>
            )}
            <PartnerCaseStudy
              key={caseItem.headline || `case-${index}`}
              data={caseItem}
              index={index + 1}
            />
          </React.Fragment>
        );
      })}

      {/* Section 4: How Mapped Works */}
      {page?.section_4 && (
        <Section4>
          <ResponsiveContainer>
            <SectionEyebrow>{page.section_4.eyebrow}</SectionEyebrow>
            <SectionHeadline>{page.section_4.title}</SectionHeadline>
            <SectionSubheadline>
              {page.section_4.subheadline}
            </SectionSubheadline>

            <div className="cards">
              {page.section_4.cards
                ?.filter(
                  (card) =>
                    card && (getAssetUrl(card.iconConnection) || card.title)
                )
                .map((card, index) => (
                  <div key={card!.title || `step-${index}`} className="card">
                    {getAssetUrl(card!.iconConnection) && (
                      <img
                        className="card-icon"
                        src={getAssetUrl(card!.iconConnection)}
                        alt=""
                      />
                    )}
                    {card!.title && (
                      <p
                        className="card-title"
                        dangerouslySetInnerHTML={{ __html: card!.title }}
                      />
                    )}
                  </div>
                ))}
            </div>

            {page.section_4.primary_button?.title && (
              <div className="buttons">
                <Link
                  href={page.section_4.primary_button.href || ''}
                  legacyBehavior
                  passHref
                >
                  <Button as="a" mType="primary">
                    {page.section_4.primary_button.title}
                  </Button>
                </Link>
              </div>
            )}
          </ResponsiveContainer>
        </Section4>
      )}

      {/* Section 5: Ecosystem Partners */}
      {page?.section_5 && (
        <Section5>
          <ResponsiveContainer>
            <SectionEyebrow>{page.section_5.eyebrow}</SectionEyebrow>
            <SectionHeadline>{page.section_5.headline}</SectionHeadline>
            <SectionSubheadline style={{ maxWidth: 'none' }}>
              {page.section_5.subheadline}
            </SectionSubheadline>

            <div className="cards">
              {page.section_5.cards
                ?.filter(
                  (card) =>
                    card && (getAssetUrl(card.logoConnection) || card.content)
                )
                .map((card, index) => {
                  const cardContent = (
                    <div
                      key={card!.content || `partner-${index}`}
                      className="card"
                    >
                      {getAssetUrl(card!.logoConnection) && (
                        <div className="logo-wrapper">
                          <img
                            className="card-logo"
                            aria-label={
                              getAssetUrl(card!.logoConnection)
                                ?.split?.('/')
                                .pop?.()
                                ?.split?.('.')
                                .slice?.(0, -1)
                                .join?.('.') || `partner-logo-${index}`
                            }
                            src={getAssetUrl(card!.logoConnection)}
                            alt=""
                          />
                        </div>
                      )}
                      {card!.content && (
                        <p className="card-content">{card!.content}</p>
                      )}
                      {card!.link?.href && (
                        <span className="card-link-arrow">
                          <Icon name="arrow-right" size={16} />
                        </span>
                      )}
                    </div>
                  );

                  if (card!.link?.href) {
                    return (
                      <Link
                        key={card!.content || `partner-${index}`}
                        href={card!.link.href}
                        legacyBehavior
                        passHref
                      >
                        <a
                          className="card-link"
                          title={card?.link?.title || undefined}
                          target="_blank"
                        >
                          {cardContent}
                        </a>
                      </Link>
                    );
                  }

                  return cardContent;
                })}
            </div>
          </ResponsiveContainer>
        </Section5>
      )}

      {/* Section 6: Why Partners Choose Mapped */}
      {page?.section_6 && (
        <Section6>
          <ResponsiveContainer>
            <SectionEyebrow>{page.section_6.eyebrow}</SectionEyebrow>
            <SectionHeadline>{page.section_6.headline}</SectionHeadline>
            <SectionSubheadline>
              {page.section_6.subheadline}
            </SectionSubheadline>

            <div className="cards">
              {page.section_6.cards
                ?.filter(
                  (card) =>
                    card && (getAssetUrl(card.iconConnection) || card.title)
                )
                .map((card, index) => (
                  <div key={card!.title || `metric-${index}`} className="card">
                    {getAssetUrl(card!.iconConnection) && (
                      <img
                        className="card-icon"
                        src={getAssetUrl(card!.iconConnection)}
                        alt=""
                      />
                    )}
                    {card!.title && (
                      <h4
                        className="card-title"
                        dangerouslySetInnerHTML={{ __html: card!.title }}
                      />
                    )}
                  </div>
                ))}
            </div>

            <div className="buttons">
              {page.section_6.primary_button?.title && (
                <Link
                  href={page.section_6.primary_button.href || ''}
                  legacyBehavior
                  passHref
                >
                  <Button as="a" mType="primary">
                    {page.section_6.primary_button.title}
                  </Button>
                </Link>
              )}
              {page.section_6.secondary_button?.title && (
                <Link
                  href={page.section_6.secondary_button.href || ''}
                  legacyBehavior
                  passHref
                >
                  <Button as="a" mType="secondary">
                    {page.section_6.secondary_button.title}
                  </Button>
                </Link>
              )}
            </div>
          </ResponsiveContainer>
        </Section6>
      )}

      {/* Footer with pre-footer CTA */}
      <Footer
        marginTop={false}
        sections={footer?.sections}
        eyebrow={page?.footer?.eyebrow}
        {...page?.footer}
      />
    </>
  );
};

export default PartnersPage;

export async function getServerSideProps() {
  try {
    const { data } = await ContentStackClient().query<{
      page: { data: Partners[] };
    }>({
      query: PAGE_QUERY,
    });

    const page = data?.page?.data?.[0];

    return {
      props: {
        ...data,
        page,
      },
    };
  } catch (e: any) {
    console.log(JSON.stringify(e?.networkError?.result, null, 2));
    return { props: {} };
  }
}

const PAGE_QUERY = gql`
  query partnersPageQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: all_partners {
      data: items {
        title
        description

        hero {
          imageConnection {
            edges {
              node {
                url
                dimension {
                  width
                  height
                }
              }
            }
          }
          eyebrow
          headline
          subheadline
          primary_button {
            title
            href
          }
          secondary_button {
            title
            href
          }
        }

        partner_logosConnection {
          edges {
            node {
              url
              dimension {
                width
                height
              }
            }
          }
        }

        section_1 {
          eyebrow
          headline
          subheadline
        }

        section_2 {
          imageConnection {
            edges {
              node {
                url
                dimension {
                  width
                  height
                }
              }
            }
          }
          eyebrow
          headline
          subheadline
          primary_button {
            title
            href
          }
        }

        section_3 {
          cases {
            partner_logoConnection {
              edges {
                node {
                  url
                  dimension {
                    width
                    height
                  }
                }
              }
            }
            headline
            subheadline
            partner_link {
              title
              href
            }
            how_mapped_helps {
              title
              benefits
              outcome {
                label
                text
              }
            }
            quote {
              eyebrow
              text
              author
              link {
                title
                href
              }
            }
          }
        }

        section_4 {
          eyebrow
          title
          subheadline
          cards {
            iconConnection {
              edges {
                node {
                  url
                  dimension {
                    width
                    height
                  }
                }
              }
            }
            title
          }
          primary_button {
            title
            href
          }
        }

        section_5 {
          eyebrow
          headline
          subheadline
          cards {
            logoConnection {
              edges {
                node {
                  url
                  dimension {
                    width
                    height
                  }
                }
              }
            }
            content
            link {
              title
              href
            }
          }
        }

        section_6 {
          eyebrow
          headline
          subheadline
          cards {
            iconConnection {
              edges {
                node {
                  url
                  dimension {
                    width
                    height
                  }
                }
              }
            }
            title
          }
          primary_button {
            title
            href
          }
          secondary_button {
            title
            href
          }
        }

        footer {
          eyebrow
          headline
          subheadline
          primary_button {
            title
            href
          }
          secondary_button {
            title
            href
          }
          third_button {
            title
            href
          }
        }
      }
    }
  }
`;

/* ── Styled Components ── */

const Hero = styled.section`
  background: ${({ theme }) => theme.colors.slate1};
  background-size: cover;
  min-height: 760px;
  overflow: hidden;
  position: relative;
  max-width: 1600px;
  width: 100%;
  margin: 0 auto;

  .image {
    pointer-events: none;
    position: absolute;
    bottom: 0;
    right: 0;
    width: 50%;
    height: 100%;

    div,
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .fade {
    pointer-events: none;
    position: absolute;
    height: 100%;
    width: 40%;
    z-index: 2;
    left: 49%;
    background: linear-gradient(90deg, #181e2b 0%, #181e2b00 90%);
  }

  .content {
    position: relative;
    z-index: 3;
    padding-top: 180px;
    padding-bottom: 160px;

    ${HeroHeadline} {
      max-width: 700px;
    }

    ${HeroSubheadline} {
      max-width: 500px;
    }
  }

  .buttons {
    display: flex;
    gap: 24px;
    a {
      text-decoration: none;
    }
  }

  ${mediaScreen('xs', 'md')} {
    min-height: auto;
    display: flex;
    flex-direction: column;

    .image {
      position: relative;
      width: 100%;
      height: 300px;

      div,
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .fade {
      width: 100%;
      height: 300px;
      left: 0;
      top: 0;
      background: linear-gradient(180deg, transparent 20%, #181e2b 100%);
    }

    ${HeroHeadline}, ${HeroSubheadline} {
      max-width: 100%;
    }

    .content {
      padding-top: 24px;
      padding-bottom: 60px;
    }

    .buttons {
      flex-direction: column;
      gap: 16px;

      .mButton {
        display: flex;
        width: 100%;
        justify-content: center;
      }
    }
  }
`;

const LogoStripSection = styled.section`
  padding: 48px 0;
  background: ${({ theme }) => theme.colors.slate1};
  z-index: 0;

  ${mediaScreen('xs', 'md')} {
    padding-top: 0;
  }
`;

const Section1 = styled.section`
  padding: 100px 0 0;
  background: #fff;
  z-index: 1;
  position: relative;
  overflow: hidden;

  .accent-col {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .accent-image {
    width: 100%;
    max-width: 80%;
    height: auto;
    margin-top: -40%;
    margin-bottom: -20%;
  }

  ${mediaScreen('xs', 'md')} {
    padding: 40px 0 60px;
    padding-top: 24px;

    .row {
      flex-direction: column-reverse;
    }

    .accent-col {
      display: flex;
      margin-bottom: 24px;
      margin-top: -60%;
      display: none;
    }

    .accent-image {
      margin-top: 0;
      margin-bottom: 0;
      width: 100%;
      position: static;
    }
  }
`;

const Section2 = styled(ResponsiveContainer)`
  padding-top: 80px;
  padding-bottom: 80px;

  .row {
    align-items: center;
    gap: 44px;
    flex-wrap: nowrap;
  }

  ${SectionEyebrow} {
    margin-top: 0;
  }

  .image {
    img {
      width: 130%;
      max-width: 130%;
      left: -30%;
      position: relative;
    }
  }

  .mButton {
    margin-top: 32px;
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 40px;
    padding-bottom: 40px;

    .row {
      flex-direction: column;
    }

    .image img {
      width: 100%;
      left: 0;
    }

    .mButton {
      width: 100%;
    }
  }
`;

const CaseDivider = styled.div`
  background: #fff;
  padding-bottom: 32px;

  hr {
    border: none;
    border-top: 2px solid ${({ theme }) => theme.colors.teal};
    margin: 0;
  }
`;

const Section4 = styled.section`
  padding: 60px 0;
  background: ${({ theme }) => theme.colors.slate1};
  background-image: url('/img/dots-bg2.svg');
  background-repeat: no-repeat;
  background-position: bottom right;
  background-size: auto;
  text-align: center;

  ${SectionEyebrow},
  ${SectionHeadline},
  ${SectionSubheadline} {
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }

  .cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
    margin-top: 48px;
  }

  .card {
    display: flex;
    align-items: center;
    gap: 16px;
    text-align: left;
    padding: 28px 32px;
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.slate2};
  }

  .card-icon {
    width: 56px;
    height: 56px;
    flex-shrink: 0;
  }

  .card-title {
    font-size: 28px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.white};
    margin: 0;
    b {
      color: ${({ theme }) => theme.colors.teal};
    }
  }

  .buttons {
    margin-top: 48px;
    text-align: center;
    a {
      text-decoration: none;
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding: 60px 0;
    padding-top: 40px;

    ${SectionEyebrow} {
      text-align: center;
    }

    .cards {
      grid-template-columns: 1fr;
      gap: 16px;
      margin-top: 32px;
    }

    .card {
      padding: 24px;
    }

    .card-title {
      font-size: 20px;
    }

    .buttons {
      margin-top: 32px;

      .mButton {
        width: 100%;
        justify-content: center;
      }
    }
  }
`;

const Section5 = styled.section`
  padding: 0;
  padding-top: 40px;
  background: ${({ theme }) => theme.colors.slate1};

  .cards {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
    margin-top: 48px;
  }

  .card {
    padding: 36px 32px;
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.slate2};
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 32px;
    position: relative;
    border: 1px solid transparent;
  }

  .logo-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 150px;
    max-height: 40px;
    flex-shrink: 0;
  }
  .card-logo {
    max-width: 100%;
    height: auto;
    max-height: 40px;

    &[aria-label='Switch_Automation'] {
      max-height: 80px;
    }
  }

  .card-content {
    font-size: 15px;
    line-height: 22px;
    color: ${({ theme }) => theme.colors.whiteAlfa};
    margin: 0;
    flex: 1;
  }

  .card-link {
    text-decoration: none;
    color: inherit;
    display: block;

    &:hover .card {
      border-color: ${({ theme }) => theme.colors.teal};
    }
  }

  .card-link-arrow {
    display: none;
  }

  ${mediaScreen('xs', 'md')} {
    padding: 60px 0;
    padding-top: 40px;

    ${SectionEyebrow}, ${SectionHeadline}, ${SectionSubheadline} {
      text-align: center;
    }

    .cards {
      grid-template-columns: 1fr;
      gap: 16px;
      margin-top: 32px;
    }

    .card {
      display: block;
      padding: 24px;
    }

    .logo-wrapper {
      margin-bottom: 24px;
      justify-content: flex-start;
      align-items: flex-start;
      width: 120px;
    }

    .card-content {
      font-size: 14px;
      line-height: 20px;
    }

    .card-logo[aria-label='Switch_Automation'] {
      max-height: 50px;
    }
  }
`;

const Section6 = styled.section`
  padding: 100px 0 80px;
  background: ${({ theme }) => theme.colors.slate1};
  text-align: center;

  ${SectionEyebrow},
  ${SectionHeadline},
  ${SectionSubheadline} {
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }

  .cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-top: 48px;
  }

  .card {
    display: flex;
    align-items: center;
    gap: 16px;
    text-align: left;
    padding: 28px 32px;
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.slate2};
  }

  .card-icon {
    width: 56px;
    height: 56px;
    flex-shrink: 0;
  }

  .card-title {
    font-size: 22px;
    line-height: 1.4;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.white};
    margin: 0;

    span,
    b {
      color: ${({ theme }) => theme.colors.teal};
    }
  }

  .buttons {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    justify-content: center;
    margin-top: 48px;
    a {
      text-decoration: none;
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding: 60px 0;
    padding-top: 0;

    ${SectionEyebrow} {
      text-align: center;
    }

    .cards {
      grid-template-columns: 1fr;
      gap: 12px;
      margin-top: 32px;
    }

    .card {
      padding: 24px;
    }

    .card-title {
      font-size: 18px;
    }

    .buttons {
      margin-top: 32px;
      flex-direction: column;

      .mButton {
        width: 100%;
        justify-content: center;
      }
    }
  }
`;
